import { BACKEND_URL } from "../lib/backend";
/**
 * PodcastPage.jsx — Public podcast archive page (no auth required).
 * Option D: Spreaker embed player + public RSS feed link.
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { RefreshCw, Rss, Headphones, Radio, ExternalLink } from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";

const API = BACKEND_URL;
const SHOW_ID = "6947755";

function SpreakerEmbed({ episodeId, dark }) {
  const theme = dark ? "dark" : "light";
  return (
    <iframe
      frameBorder="0"
      height="180"
      scrolling="no"
      src={`https://widget.spreaker.com/player?episode_id=${episodeId}&theme=${theme}&playlist=false&autoplay=false&hide-logo=false`}
      width="100%"
      title={`Podcast episode ${episodeId}`}
      className="rounded-xl overflow-hidden"
    />
  );
}

export default function PodcastPage() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [dm] = useDarkMode();

  useEffect(() => {
    axios.get(`${API}/api/podcast/public-episodes`)
      .then(({ data }) => setEpisodes(data.episodes || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const c = dm
    ? { bg: "bg-[#0f1718]", card: "bg-[#1a2324]", border: "border-slate-700/50", text: "text-slate-200", muted: "text-slate-500", sub: "text-slate-400" }
    : { bg: "bg-[#f5f6f7]", card: "bg-white",     border: "border-[#e7e8e9]",   text: "text-[#191c1d]", muted: "text-[#6b7a7d]",  sub: "text-[#3b494c]" };

  return (
    <div className={`min-h-screen ${c.bg}`}>
      {/* Nav — shared public nav */}
      <PublicPageNav active="podcast" />

      {/* Podcast-specific secondary nav (RSS + Spreaker) */}
      <div className={`border-b ${c.border} ${dm ? "bg-[#0f1718]" : "bg-white"}`}>
        <div className="max-w-4xl mx-auto px-6 py-2 flex items-center justify-end gap-3">
          <a
            href={`${API}/api/rss/podcast`}
            target="_blank"
            rel="noreferrer"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${dm
              ? "border-slate-700 text-slate-400 hover:text-orange-400 hover:border-orange-400/30"
              : "border-[#e7e8e9] text-[#6b7a7d] hover:text-orange-500 hover:border-orange-500/30"
            }`}
            title="Subscribe via RSS"
            data-testid="rss-feed-link"
          >
            <Rss className="w-3.5 h-3.5" /> RSS Feed
          </a>
          <a
            href={`https://www.spreaker.com/show/${SHOW_ID}`}
            target="_blank"
            rel="noreferrer"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${dm
              ? "bg-cyan-500 text-[#001f24] hover:bg-cyan-400"
              : "bg-[#006874] text-white hover:bg-[#004f58]"
            }`}
            data-testid="spreaker-show-link"
          >
            <Radio className="w-3.5 h-3.5" /> Follow on Spreaker
          </a>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 pt-14 pb-10 text-center">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-5 ${dm ? "bg-cyan-500/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>
          <Headphones className="w-3.5 h-3.5" /> Podcast
        </div>
        <h1 className={`text-4xl sm:text-5xl font-bold editorial-heading mb-4 ${c.text}`}>
          TourismIntel Weekly
        </h1>
        <p className={`text-base sm:text-lg max-w-xl mx-auto ${c.muted}`}>
          AI, technology and innovation in the travel industry.<br/>
          <span className={`text-sm ${c.muted}`}>Hosted by <strong className={c.sub}>Mirko Lalli</strong></span>
        </p>

        {/* Subscribe options */}
        <div className="flex items-center justify-center gap-3 flex-wrap mt-8">
          {[
            { label: "Apple Podcasts", url: `https://podcasts.apple.com/search?term=tourismintel`, icon: "🎙" },
            { label: "Spotify", url: `https://open.spotify.com/search/TourismIntel`, icon: "🎧" },
            { label: "Spreaker", url: `https://www.spreaker.com/show/${SHOW_ID}`, icon: "📻" },
          ].map(s => (
            <a
              key={s.label}
              href={s.url}
              target="_blank"
              rel="noreferrer"
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-medium transition-colors ${c.border} ${c.sub} ${dm ? "hover:border-cyan-500/40 hover:text-cyan-400" : "hover:border-[#006874]/30 hover:text-[#006874]"}`}
            >
              <span>{s.icon}</span> {s.label}
            </a>
          ))}
          <a
            href={`${API}/api/rss/podcast`}
            target="_blank"
            rel="noreferrer"
            className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-medium transition-colors ${c.border} ${c.sub} hover:border-orange-400/40 hover:text-orange-400`}
          >
            <Rss className="w-3 h-3" /> RSS
          </a>
        </div>
      </div>

      {/* Episodes list */}
      <div className="max-w-4xl mx-auto px-6 pb-20 space-y-5">
        {loading ? (
          <div className="flex justify-center py-20">
            <RefreshCw className={`w-6 h-6 animate-spin ${c.muted}`} />
          </div>
        ) : episodes.length === 0 ? (
          <div className={`text-center py-20 ${c.muted}`}>
            <Headphones className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No episodes published yet — check back soon.</p>
          </div>
        ) : (
          episodes.map(ep => {
            const isOpen = openId === ep.id;
            return (
              <div
                key={ep.id}
                className={`rounded-2xl border ${c.border} ${c.card} overflow-hidden transition-shadow hover:shadow-md`}
                data-testid="podcast-episode-card"
              >
                <div className="p-5">
                  {/* Episode header */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      {ep.week && (
                        <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${c.muted}`}>
                          {ep.week}
                        </p>
                      )}
                      <h2 className={`text-base font-bold leading-snug ${c.text}`}>
                        {ep.title || "TourismIntel Weekly"}
                      </h2>
                      {ep.description && (
                        <p className={`text-sm mt-1 line-clamp-2 ${c.muted}`}>{ep.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {ep.spreaker_episode_url && (
                        <a
                          href={ep.spreaker_episode_url}
                          target="_blank"
                          rel="noreferrer"
                          className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-500 hover:text-cyan-400" : "text-[#bac9cc] hover:text-[#006874]"}`}
                          title="Open on Spreaker"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {ep.spreaker_episode_id && (
                        <button
                          onClick={() => setOpenId(isOpen ? null : ep.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isOpen
                            ? (dm ? "bg-cyan-500/15 text-cyan-400" : "bg-[#006874]/10 text-[#006874]")
                            : (dm ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]")
                          }`}
                          data-testid={`play-episode-${ep.id}`}
                        >
                          <Headphones className="w-3.5 h-3.5" />
                          {isOpen ? "Close" : "Listen"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Spreaker embed — lazy: only rendered when open */}
                  {isOpen && ep.spreaker_episode_id && (
                    <div className="mt-3">
                      <SpreakerEmbed episodeId={ep.spreaker_episode_id} dark={dm} />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <PublicPageFooter dark={dm} />
    </div>
  );
}
